import React, { useState, useEffect } from "react";
import { Button, Input, message, Upload } from "antd";
import { MdOutlineNavigateNext } from "react-icons/md";
import { useDrawer } from "../../../../context/drawer";
import ReactQuill from "react-quill";
import { PlusOutlined } from "@ant-design/icons";
import "react-quill/dist/quill.snow.css";
import axios from "axios";

export default function AddBlogs() {
  const [loading, setLoading] = useState(false);
  const { toggleDrawer } = useDrawer();

  const [formData, setFormData] = useState({
    title: "",
    slug: "",
    short_description: "",
    main_description: "",
    metaTitle: "",
    metaDescription: "",
    canonicalTag: "",
  });

  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [fileList, setFileList] = useState([]);

  // Auto-generate slug from title if not manually edited
  useEffect(() => {
    if (!slugManuallyEdited) {
      const slug = formData.title
        .toLowerCase()
        .replace(/[^\w ]+/g, "")
        .replace(/ +/g, "-");
      setFormData((prev) => ({ ...prev, slug }));
    }
  }, [formData.title, slugManuallyEdited]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // If user manually edits the slug
    if (name === "slug") {
      setSlugManuallyEdited(true);
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmitClick = async () => {
    const {
      title,
      slug,
      short_description,
      main_description,
      metaTitle,
      metaDescription,
      canonicalTag,
    } = formData;
  
    if (
      !title ||
      !short_description ||
      !main_description ||
      fileList.length === 0 ||
      !metaTitle ||
      !metaDescription ||
      !canonicalTag
    ) {
      message.warning("Please fill all fields and upload an image.");
      return;
    }
  
    try {
      setLoading(true);
  
      const data = new FormData();
      data.append("action", "add_blog");
      data.append("title", title);
      data.append("slug", slug);
      data.append("short_description", short_description);
      data.append("content", main_description);
      data.append("meta_title", metaTitle);
      data.append("meta_description", metaDescription);
      data.append("canonical_tag", canonicalTag);
  
      if (fileList.length > 0 && fileList[0].originFileObj) {
        data.append("image", fileList[0].originFileObj);
      } else {
        message.error("Image file not found. Please ensure an image is uploaded.");
        setLoading(false);
        return;
      }
  
      const resp = await axios.post("https://skdm.in/server/sdg/blogs.php", data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (resp && resp.data) {
        const { success, message: serverMsg } = resp.data.status;
  
        if (resp.data) {
          message.success(serverMsg || "Blog added successfully!");
          toggleDrawer();
          setFormData({
            title: "",
            slug: "",
            short_description: "",
            main_description: "",
            metaTitle: "",
            metaDescription: "",
            canonicalTag: "",
          });
          setSlugManuallyEdited(false);
          setFileList([]);
        } else {
          message.error(serverMsg || "Failed to add blog due to unknown server error.");
        }
      } else {
        message.error("Failed to add blog. Server did not return expected response.");
      }
    } catch (err) {
      console.error("Error adding blog:", err);
      if (err.response) {
        message.error(`Failed to add blog: ${err.response.data.message || "Server error"}`);
      } else if (err.request) {
        message.error("Failed to add blog. No response from server. Check your network.");
      } else {
        message.error("Failed to add blog. Try again.");
      }
    } finally {
      setLoading(false);
    }
  };
  

  return (
    <div className="add-blog-form">
      <label>Title</label>
      <Input
        name="title"
        value={formData.title}
        onChange={handleInputChange}
        placeholder="Enter blog title"
      />

      <label className="mt-3">Slug</label>
      <Input
        name="slug"
        value={formData.slug}
        onChange={handleInputChange}
        placeholder="Custom slug (auto-generated)"
      />

      <label className="mt-3">Short Description</label>
      <Input.TextArea
        rows={4}
        name="short_description"
        value={formData.short_description}
        onChange={handleInputChange}
        placeholder="Enter short description"
      />

      <label className="mt-3">Main Description</label>
      <ReactQuill
        value={formData.main_description}
        onChange={(value) =>
          setFormData((prev) => ({ ...prev, main_description: value }))
        }
      />

      <hr className="my-4" />
      <h4>SEO Information</h4>

      <label className="mt-3">Meta Title</label>
      <Input
        name="metaTitle"
        value={formData.metaTitle}
        onChange={handleInputChange}
        placeholder="Enter meta title for SEO"
      />

      <label className="mt-3">Meta Description</label>
      <Input.TextArea
        rows={4}
        name="metaDescription"
        value={formData.metaDescription}
        onChange={handleInputChange}
        placeholder="Enter meta description for SEO"
      />

      <label className="mt-3">Canonical Tag</label>
      <Input
        name="canonicalTag"
        value={formData.canonicalTag}
        onChange={handleInputChange}
        placeholder="Enter canonical URL"
      />

      <label className="mt-3 d-block">Image</label>
      <Upload
        listType="picture-card"
        maxCount={1}
        fileList={fileList}
        onRemove={() => setFileList([])}
        beforeUpload={(file) => {
          const newFile = {
            uid: file.uid || Date.now().toString(),
            name: file.name,
            status: "done",
            originFileObj: file,
          };
          setFileList([newFile]);
          return false;
        }}
        accept="image/*"
      >
        {fileList.length === 0 && (
          <div>
            <PlusOutlined />
            <div style={{ marginTop: 8 }}>Upload</div>
          </div>
        )}
      </Upload>

      <div className="buttonGroup w-100 d-flex justify-content-end mt-4">
        <Button
          className="otherButtons active"
          icon={<MdOutlineNavigateNext />}
          onClick={handleSubmitClick}
          loading={loading}
        >
          Submit
        </Button>
      </div>
    </div>
  );
}
